import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { SiteContent, GlobalSettings, SectionKey } from '../types/cms';
import { fetchContentSnapshot, resolveContent, type ContentSnapshot } from '../lib/contentSnapshot';
import type { CmsPage } from '../types/cmsPages';

interface ContentContextType {
  content: SiteContent;
  settings: GlobalSettings;
  loading: boolean;
  pages: CmsPage[];
  hasSnapshot: boolean;
  updateSection: (key: SectionKey, data: SiteContent[SectionKey]) => Promise<void>;
  updateSettings: (settings: Partial<GlobalSettings>) => Promise<void>;
  refetch: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | null>(null);

export function ContentProvider({ children, initialSnapshot }: { children: ReactNode; initialSnapshot?: ContentSnapshot }) {
  const [initial] = useState(() => resolveContent(initialSnapshot));
  const [content, setContent] = useState<SiteContent>(initial.content);
  const [settings, setSettings] = useState<GlobalSettings>(initial.settings);
  const [pages, setPages] = useState<CmsPage[]>(initialSnapshot?.pages ?? []);
  const [loading, setLoading] = useState(!initialSnapshot);

  const fetchContent = useCallback(async () => {
    try {
      const snapshot = await fetchContentSnapshot();
      const resolved = resolveContent(snapshot);
      setContent(resolved.content);
      setSettings(resolved.settings);
      setPages(snapshot.pages);
    } catch (err) {
      console.error('Failed to refresh CMS content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const updateSection = useCallback(async (key: SectionKey, data: SiteContent[SectionKey]) => {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase
      .from('site_content')
      .upsert({
        section_key: key,
        display_name: sectionDisplayNames[key],
        content: data,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'section_key' });

    if (error) throw error;
    setContent(prev => ({ ...prev, [key]: data }));
  }, []);

  const updateSettings = useCallback(async (newSettings: Partial<GlobalSettings>) => {
    const { supabase } = await import('../lib/supabase');
    const updates = Object.entries(newSettings).map(([key, value]) => ({
      setting_key: key,
      setting_value: value,
      updated_at: new Date().toISOString(),
    }));

    for (const update of updates) {
      const { error } = await supabase
        .from('global_settings')
        .upsert(update, { onConflict: 'setting_key' });
      if (error) throw error;
    }

    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  return (
    <ContentContext.Provider value={{ content, settings, pages, hasSnapshot: Boolean(initialSnapshot), loading, updateSection, updateSettings, refetch: fetchContent }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}

const sectionDisplayNames: Record<SectionKey, string> = {
  hero: 'Hero Section',
  about: 'About Us',
  services: 'Services',
  testimonials: 'Testimonials',
  cta: 'Call to Action',
  serviceAreas: 'Service Areas',
  whyChooseUs: 'Why Choose Us',
  whatWeHandle: 'What We Handle',
  trustedPartners: 'Trusted Partners',
  financingTeaser: 'Financing Teaser',
  faq: 'FAQ',
  plansHero: 'Plans Hero',
  plansGrid: 'Plans Grid',
  plansCta: 'Plans CTA',
  financingHero: 'Financing Hero',
  financingOverview: 'Financing Overview',
  financingHowItWorks: 'How It Works',
  financingPlans: 'Financing Plans',
  financingBenefits: 'Financing Benefits',
  serviceLocationHero: 'Service Location Hero',
  serviceLocationMap: 'Service Location Map',
  contactHero: 'Contact Hero',
  contactInfo: 'Contact Info & Form',
  seoGlobal: 'SEO Global Settings',
  seoHome: 'SEO - Home Page',
  seoPlans: 'SEO - Plans Page',
  seoFinancing: 'SEO - Financing Page',
  seoServiceAreas: 'SEO - Service Areas Page',
  seoContact: 'SEO - Contact Page',
};
